'use client';

import { createContext, useCallback, useContext, useMemo, useRef, useState, type FC, type MutableRefObject, type ReactNode } from 'react';
import type { S3DocSelection } from './types';

interface S3DocState {
	addDocuments: (docs: S3DocSelection[]) => void;
	clearDocuments: () => void;
	/** Ref that always points to the latest clearDocuments — safe to read from async closures. */
	clearDocumentsRef: MutableRefObject<() => void>;
	documents: S3DocSelection[];
	/** Ref that always points to the latest documents — safe to read from async closures. */
	documentsRef: MutableRefObject<S3DocSelection[]>;
	removeDocument: (documentId: number) => void;
}

const S3DocContext = createContext<S3DocState | null>(null);

export const useS3Docs = (): S3DocState => {
	const ctx = useContext(S3DocContext);
	if (!ctx) throw new Error('useS3Docs must be used within S3DocProvider');
	return ctx;
};

export const S3DocProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [docMap, setDocMap] = useState<Map<number, S3DocSelection>>(new Map());
	const clearDocumentsRef = useRef<() => void>(() => {});
	const documentsRef = useRef<S3DocSelection[]>([]);

	const documents = useMemo(() => {
		const arr = Array.from(docMap.values());
		documentsRef.current = arr;
		return arr;
	}, [docMap]);

	const addDocuments = useCallback((docs: S3DocSelection[]) => {
		setDocMap((prev) => {
			const next = new Map(prev);
			for (const doc of docs) {
				next.set(doc.documentId, doc);
			}
			return next;
		});
	}, []);

	const removeDocument = useCallback((documentId: number) => {
		setDocMap((prev) => {
			const next = new Map(prev);
			next.delete(documentId);
			return next;
		});
	}, []);

	const clearDocuments = useCallback(() => {
		setDocMap(new Map());
	}, []);
	clearDocumentsRef.current = clearDocuments;

	const value = useMemo<S3DocState>(
		() => ({ addDocuments, clearDocuments, clearDocumentsRef, documents, documentsRef, removeDocument }),
		[addDocuments, clearDocuments, clearDocumentsRef, documents, documentsRef, removeDocument]
	);

	return <S3DocContext.Provider value={value}>{children}</S3DocContext.Provider>;
};
